import React, { useEffect, useMemo, useState } from 'react';
import type { History } from 'history';
import type { match } from 'react-router-dom';
import TabSwitcher from '../components/tab-switcher/tab-switcher';
import OfferComparison from './offer-comparison';
import Trucklane from './trucklane/trucklane';
import type { User } from '../graphql/query-result-types';
import { useQuery } from '@apollo/client';
import { ActiveOfferShiftsQuery } from '../graphql/queries';
import { OFFER_SHIFT_STATUS, OfferCardOfferShift } from './offer-comparison/offer-card-v3/offer-card-types';
import OfferShiftContext from './offer-comparison/offer-shift-context';
import { TrucklaneActionInProgressContext } from './trucklane/trucklane-utility';
import { checkFeature, featureFlags } from '../helpers';
import OfferComparisonProgressive from './offer-comparison/offer-comparison-progressive';

interface RouterPageProps {
  history: History;
  user: User;
  match: match;
}

const OfferManagementRoot: React.FC<RouterPageProps> = ({ history, user, match }) => {
  const offerShiftRecalculationEnabled = checkFeature(featureFlags.offerShiftRecalculation);
  const reservePriceExceptionsEnabled = checkFeature(featureFlags.reservePriceExceptions);
  const offerCompProgressiveLoadEnabled = checkFeature(featureFlags.offerCompProgressiveLoad);
  const { data: offerShiftData } = useQuery<{ activeOfferShiftsQuery: { offerShifts: OfferCardOfferShift[] } }>(ActiveOfferShiftsQuery, {
    skip: !offerShiftRecalculationEnabled,
  });
  const [activeOfferShifts, setActiveOfferShifts] = useState<OfferCardOfferShift[]>([]);
  const [previousContext, setPreviousContext] = useState<{ scrollPosition: number; path: string }>({
    scrollPosition: 0,
    path: history.location.pathname,
  });
  const [actionInProgress, setActionInProgress] = useState<boolean>(false);

  const offerShifts = useMemo(() => {
    return offerShiftData?.activeOfferShiftsQuery?.offerShifts || [];
  }, [offerShiftData]);

  useEffect(() => {
    if (history.location.hash !== '') {
      const shiftIds = new Set<string>();
      const shiftMatches = history.location.hash.matchAll(/([A-f0-9]{24})/g);
      [...shiftMatches].map((mtch) => {
        shiftIds.add(mtch[1]);
      });
      if (shiftIds.size > 0) {
        const shifts = offerShifts.filter((s) => shiftIds.has(s._id));
        if (shifts && shifts.length > 0) setActiveOfferShifts(shifts);
        else history.replace(history.location.pathname);
      } else {
        setActiveOfferShifts([]);
      }
    } else {
      setActiveOfferShifts([]);
    }
    if (history.location.pathname === '/offer-comparison') {
      const path = reservePriceExceptionsEnabled ? '/offer-comparison/trucklane' : '/offer-comparison/inventory';
      history.replace(path);
    }
  }, [history, history.location.pathname, history.location.hash, offerShifts, reservePriceExceptionsEnabled]);

  const offerShiftMap = useMemo(() => {
    return offerShifts.reduce((shiftMap, shift) => {
      if (shift.status === OFFER_SHIFT_STATUS.ACCEPTED) {
        // Shift has already been applied
        if (!shiftMap.has(shift.newOfferListingId)) shiftMap.set(shift.newOfferListingId, []);
        shiftMap.get(shift.newOfferListingId)?.push(shift);
      }
      if (!shiftMap.has(shift.originalOfferListingId)) shiftMap.set(shift.originalOfferListingId, []);
      shiftMap.get(shift.originalOfferListingId)?.push(shift);

      if (!shiftMap.has(shift.shiftInventoryId)) shiftMap.set(shift.shiftInventoryId, []);
      shiftMap.get(shift.shiftInventoryId)?.push(shift);
      return shiftMap;
    }, new Map<string, OfferCardOfferShift[]>());
  }, [offerShifts]);

  const offerShiftsMappedToTrucklanes = useMemo(() => {
    return offerShifts.reduce((shiftMap, shift) => {
      if (shift.status === OFFER_SHIFT_STATUS.PROPOSED) {
        // Shift has already been applied
        if (!shiftMap.has(shift.proposedTrucklaneId)) shiftMap.set(shift.proposedTrucklaneId, []);
        shiftMap.get(shift.proposedTrucklaneId)?.push(shift);
      }
      return shiftMap;
    }, new Map<string, OfferCardOfferShift[]>());
  }, [offerShifts]);

  const offerShiftContextValue = useMemo(() => {
    return {
      shiftMap: offerShiftMap,
      offerShiftsMappedToTrucklanes,
      currentShifts: activeOfferShifts,
      setCurrentShifts: (shifts: OfferCardOfferShift[], inventoryId?: string) => {
        if (shifts.length > 0) {
          const shiftIds = shifts.map((s) => s._id);
          setPreviousContext({ scrollPosition: window.scrollY, path: history.location.pathname });
          history.push(`/offer-comparison/inventory#shifts:${shiftIds.join(',')}`, inventoryId ? { selectedInventoryId: inventoryId } : undefined);
        } else {
          history.push(previousContext.path);
          window.scrollY = previousContext.scrollPosition;
          setActiveOfferShifts([]);
        }
      },
    };
  }, [offerShiftMap, offerShiftsMappedToTrucklanes, activeOfferShifts, history, previousContext]);

  const OfferCompComponent = offerCompProgressiveLoadEnabled ? OfferComparisonProgressive : OfferComparison;

  const getTabs = () => {
    const inventoryTab = { url: '/inventory', title: 'Inventory', component: OfferCompComponent };
    const trucklaneTab = {
      url: '/trucklane',
      title: 'Truck Lanes',
      component: Trucklane,
      optionalPath: ':trucklaneId?',
    };

    return reservePriceExceptionsEnabled ? [trucklaneTab, inventoryTab] : [inventoryTab, trucklaneTab];
  };

  return (
    <TrucklaneActionInProgressContext.Provider value={{ actionInProgress, setActionInProgress }}>
      <OfferShiftContext.Provider value={offerShiftContextValue}>
        <TabSwitcher history={history} user={user} match={match} tabs={getTabs()} />
      </OfferShiftContext.Provider>
    </TrucklaneActionInProgressContext.Provider>
  );
};

export default OfferManagementRoot;
